/**
 * Heatmap generator: turns a flat list of days into the week/weekday grid GitHub uses
 * (one column per week starting on Sunday, one row per weekday).
 */
import { CalendarDay } from '../../github/types';
import { DAY_MS, MONTH_NAMES, toUtc } from '../shared/dates';

export interface GridDay extends CalendarDay {
    /** Column index (week). */
    week: number;
    /** Row index, 0 = Sunday. */
    weekday: number;
}

export interface MonthLabel {
    week: number;
    label: string;
}

export interface CalendarGrid {
    weeks: number;
    days: GridDay[];
    months: MonthLabel[];
    total: number;
}

/** Minimum number of columns between month labels, otherwise the earlier one is dropped. */
const MIN_MONTH_GAP = 3;

export const buildGrid = (days: CalendarDay[]): CalendarGrid => {
    const sorted = [...days]
        .filter((day) => /^\d{4}-\d{2}-\d{2}$/.test(day.date))
        .sort((a, b) => a.date.localeCompare(b.date));
    if (sorted.length === 0) return { weeks: 0, days: [], months: [], total: 0 };

    const first = toUtc(sorted[0].date);
    // Start of the week (Sunday) that contains the first day.
    const origin = first - new Date(first).getUTCDay() * DAY_MS;

    const gridDays: GridDay[] = sorted.map((day) => {
        const time = toUtc(day.date);
        return {
            ...day,
            week: Math.floor(Math.round((time - origin) / DAY_MS) / 7),
            weekday: new Date(time).getUTCDay(),
        };
    });

    const weeks = gridDays[gridDays.length - 1].week + 1;

    // A label goes on the first column whose top-most day falls in a new month.
    const candidates: MonthLabel[] = [];
    let previousMonth = -1;
    for (const day of gridDays) {
        const month = new Date(toUtc(day.date)).getUTCMonth();
        // Only start a label at the first day of a column (or the very first day).
        const isColumnStart = day.weekday === 0 || day === gridDays[0];
        if (month !== previousMonth && isColumnStart) {
            candidates.push({ week: day.week, label: MONTH_NAMES[month] });
            previousMonth = month;
        }
    }
    const months = candidates.filter(
        (label, i) =>
            i === candidates.length - 1 || candidates[i + 1].week - label.week >= MIN_MONTH_GAP,
    );

    return {
        weeks,
        days: gridDays,
        months,
        total: gridDays.reduce((sum, day) => sum + day.count, 0),
    };
};
